// src/ChangePasswordDialog.js
import React, { useState } from 'react';
import { updatePassword } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, Typography, Box
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';

/**
 * @param {boolean}  forced               - Quando true, o diálogo não pode ser fechado;
 *                                          exibe aviso de senha temporária.
 * @param {function} onFirstAccessComplete - Callback após troca bem-sucedida no modo forçado.
 */
function ChangePasswordDialog({ open, onClose, forced = false, onFirstAccessComplete }) {
    const [newPassword, setNewPassword]       = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError]     = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }
        if (newPassword.length < 8) {
            setError('A senha deve ter pelo menos 8 caracteres.');
            return;
        }

        setLoading(true);
        try {
            const user = auth.currentUser;
            if (!user) {
                setError('Usuário não autenticado. Faça login novamente.');
                setLoading(false);
                return;
            }

            await updatePassword(user, newPassword);

            // Modo forçado: limpa flag de primeiro acesso no Firestore
            if (forced) {
                await updateDoc(doc(db, 'usuarios', user.uid), { primeiroAcesso: false });
            }

            setSuccess('Senha alterada com sucesso!');
            setNewPassword('');
            setConfirmPassword('');

            setTimeout(() => {
                setSuccess('');
                if (forced && onFirstAccessComplete) {
                    onFirstAccessComplete();
                } else {
                    onClose();
                }
            }, 1500);

        } catch (err) {
            if (err.code === 'auth/requires-recent-login') {
                setError('Por segurança, faça logout e login novamente antes de alterar a senha.');
            } else {
                setError('Erro ao alterar senha: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={forced ? undefined : onClose}
            disableEscapeKeyDown={forced}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockResetIcon color={forced ? 'warning' : 'primary'} />
                {forced ? 'Troque sua senha antes de continuar' : 'Alterar Minha Senha'}
            </DialogTitle>

            <DialogContent>
                {forced && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="body2">
                            Sua conta foi criada com uma <strong>senha temporária</strong>.
                            Por segurança, defina uma senha pessoal antes de acessar o sistema.
                        </Typography>
                    </Box>
                )}

                <TextField
                    autoFocus
                    margin="dense"
                    label="Nova Senha"
                    type="password"
                    fullWidth
                    variant="standard"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    helperText="Mínimo 8 caracteres"
                />
                <TextField
                    margin="dense"
                    label="Confirmar Nova Senha"
                    type="password"
                    fullWidth
                    variant="standard"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />

                {error   && <Alert severity="error"   sx={{ mt: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            </DialogContent>

            <DialogActions>
                {!forced && (
                    <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                )}
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar Nova Senha'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChangePasswordDialog;
